import clsx from 'clsx';
import type { ReactNode } from 'react';

export interface DashboardMetricCardProps {
  icon: ReactNode;
  value: string;
  label: string;
  badgeLabel?: string;
  badgeClassName?: string;
  compact?: boolean;
  loading: boolean;
  colorClass: string;
  iconBgClass: string;
  /** e.g. max width for tab summary rows (`w-full max-w-[280px]`) */
  className?: string;
}

/** Same card chrome as the Dashboard metric grid (This Month / All Time). */
export default function DashboardMetricCard({
  icon,
  value,
  label,
  badgeLabel = 'Snapshot',
  badgeClassName,
  compact = false,
  loading,
  colorClass,
  iconBgClass,
  className,
}: DashboardMetricCardProps) {
  return (
    <div
      className={clsx(
        compact
          ? 'app-panel rounded-[22px] p-2.5 md:p-3'
          : 'app-panel rounded-[24px] p-3.5 md:p-4',
        className,
      )}
    >
      <div
        className={clsx(
          'flex items-start justify-between gap-3',
          compact && 'gap-2',
        )}
      >
        <div
          className={clsx(
            compact
              ? 'flex h-8 w-8 items-center justify-center rounded-2xl ring-1 ring-white/40'
              : 'flex h-9 w-9 items-center justify-center rounded-[18px] ring-1 ring-white/40',
            iconBgClass,
          )}
        >
          <span className={colorClass}>{icon}</span>
        </div>
        <span
          className={clsx(
            compact
              ? 'app-chip px-2 py-0.5 text-[8px] uppercase tracking-[0.16em]'
              : 'app-chip text-[9px] uppercase tracking-[0.16em]',
            badgeClassName,
          )}
        >
          {badgeLabel}
        </span>
      </div>
      <div className={clsx(compact ? 'mt-3' : 'mt-4')}>
        <h3
          className={`${compact ? 'text-[1.15rem] md:text-[1.3rem]' : 'text-[1.75rem] md:text-[1.9rem]'} font-semibold leading-none tracking-[-0.04em] ${
            loading
              ? 'animate-pulse text-[rgb(var(--app-muted)/0.35)]'
              : 'text-[rgb(var(--app-text))]'
          }`}
        >
          {loading ? '—' : value}
        </h3>
        <p
          className={clsx(
            compact ? 'mt-1 text-[12px]' : 'mt-1.5 text-[13px]',
            'font-medium text-[rgb(var(--app-text))]',
          )}
        >
          {label}
        </p>
      </div>
    </div>
  );
}

/** Use on Calls / Reservations / Orders tab summary rows so pairs of cards do not stretch full width. */
export const tabPageMetricCardClassName = 'w-full max-w-[280px]';
